import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { MatPageable, PageableResponse } from '@core/table/mat-table';
import { HttpHeaders } from '@angular/common/http';
import { Dominio, DominioFilter, Dominios, DominiosFilter } from '@core/models/dominio';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { buildQueryString } from '@shared/util/util';
import { DominioHierarquia } from '@core/models/dominios-hierarquia';
import { HttpParams } from '@angular/common/http';
import { DomainCacheService } from './domain-cache.service';


const baseRef = 'dominios';
const route = {
  consultar: `${baseRef}/consultar`,
  consultarDominio: (dominio: string) => `${baseRef}/consultar/${dominio}`,
  consultarDominioPorId: (dominio: string, id: number) => `${baseRef}/consultar/${dominio}/${id}`,
  edit: (dominio: string, id: number) => `${baseRef}/${dominio}/editar/${id}`,
  insert: (dominio: string) => `${baseRef}/${dominio}/salvar`,
  delete: (dominio: string, id: number) => `${baseRef}/${dominio}/inativar/${id}`,
  reativar: (dominio: string, id: number) => `${baseRef}/${dominio}/reativar/${id}`,
  hierarquia: `cargo-tipo-entidade`,
  removerHierarquia: (idTipoEntidade: number) => `cargo-tipo-entidade/tipo-entidade/${idTipoEntidade}`,
  consultarCargoHierarquia: 'cargo-tipo-entidade?',
  consultarCargoHierarquiaEntidade: (idTipoEntidade: number, subEntidadeId: number) => `cargo-tipo-entidade/listarParaEntidade/${idTipoEntidade}?subEntidadeId=${subEntidadeId}`,
  reativarHierarquia: (idTipoEntidade: number) => `cargo-tipo-entidade/reativar/tipo-entidade/${idTipoEntidade}`,
  reativarItem: (idCargoHierarquia: number) => `cargo-tipo-entidade/reativar/${idCargoHierarquia}`,
  inativarItem: (idCargoHierarquia: number) => `cargo-tipo-entidade/inativar/${idCargoHierarquia}`,
  inativarItemNovoCargos: (entidadeId: number, cargoId: number) => `cargos-entidade/${entidadeId}/${cargoId}`,
}

@Injectable({
  providedIn: 'root'
})
export class DominiosService {

  constructor(
    private apiService: ApiService,
    private domainCache: DomainCacheService
  ) { }

  consultar(filtro: DominiosFilter, page: any) {
    const url = `${route.consultar}${page.query}`;
    const data = this.apiService.removeNullPropertys(filtro);
    return this.apiService.post<PageableResponse<Dominios>>(url, data);
  }

  consultarDominio(dominio: string, filtro: DominioFilter, page: MatPageable) {
    const url = `${route.consultarDominio(dominio)}${page.query}`;
    const data = this.apiService.removeNullPropertys(filtro);
    return this.apiService.post<PageableResponse<Dominio>>(url, data);
  }

  consultarSort(dominio: string, filtro: any, page: MatPageable, params: any) {
    const queryString = buildQueryString({ ...params });
    const fullUrl = `${route.consultarDominio(dominio)}${page.query}${queryString}`;
    return this.apiService.post<PageableResponse<any>>(
      fullUrl,
      this.apiService.removeNullPropertys(filtro)
    );
  }

  consultarSemDominio(filtro: any, page: MatPageable, params: any) {
    const queryString = buildQueryString({ ...params });
    const fullUrl = `${route.consultar}${page.query}${queryString}`;
    return this.apiService.post<PageableResponse<any>>(
      fullUrl,
      this.apiService.removeNullPropertys(filtro)
    );
  }

  consultarDominioPorId(dominio: string, id: number) {
    return this.apiService.get(route.consultarDominioPorId(dominio, id));
  }

  cadastrar(dominio: string, data: any) {
    return this.apiService.post(route.insert(dominio), data).pipe(
      tap(() => this.domainCache.invalidateDominio(dominio))
    );
  }

  editar(dominio: string, id: number, data: any) {
    return this.apiService.put(route.edit(dominio, id), data).pipe(
      tap(() => this.domainCache.invalidateDominio(dominio))
    );
  }

  deletar(dominio: string, id: number) {
    return this.apiService.delete(route.delete(dominio, id)).pipe(
      tap(() => this.domainCache.invalidateDominio(dominio))
    );
  }

  reativar(dominio: string, id: number) {
    return this.apiService.put(route.reativar(dominio, id)).pipe(
      tap(() => this.domainCache.invalidateDominio(dominio))
    );
  }

  atualizarCodigoPai(dominio: string, id: number) {
    return this.apiService.put(`${baseRef}/${dominio}/atualizarCodigoPai/${id}`);
  }

  consultarCargoHierarquia(idTipoEntidade: any, subEntidadeId: any, idTipoOrgao: any, incluirInativos?: boolean): Observable<any> {
    let queryParams = '';

    if (idTipoOrgao) {
      queryParams += `&tipoId=${idTipoOrgao}`;
    }

    if (incluirInativos) {
      queryParams += `&incluirInativos=true`;
    }

    if (subEntidadeId) {
      queryParams += `&subEntidadeId=${subEntidadeId}`;
    }

    if (idTipoEntidade) {
      queryParams += `&tipoEntidadeId=${idTipoEntidade}`;
    }

    return this.apiService.get<any>(`${route.consultarCargoHierarquia}${queryParams}`);
  }


  consultarCargoHierarquiaEntidade(idCargo: any, subEntidadeId: any, idTipo: any, incluirInativos?: boolean): Observable<any> {
    let queryParams = '';
    if (idTipo) {
      queryParams += `&tipoId=${idTipo}`;
    }
    if (incluirInativos) {
      queryParams += `&incluirInativos=true`;
    }
    return this.apiService.get<any>(`${route.consultarCargoHierarquiaEntidade(idCargo, subEntidadeId)}${queryParams}`);
  }



  consultarHierarquia(dominio: number, subTipo?: number) {
    const queryParams = subTipo ? `?tipoSubentidadeId=${subTipo}` : '';
    return this.apiService.get<Dominio[]>(`${route.hierarquia}/${dominio}${queryParams}`);
  }

  cadastrarHierarquia(data: DominioHierarquia) {
    return this.apiService.post<DominioHierarquia>(route.hierarquia, data).pipe(
      tap(() => this.domainCache.invalidateDominio('CARGO_REFERENCIA'))
    );
  }

  editarHierarquia(data: DominioHierarquia) {
    return this.apiService.put<DominioHierarquia>(route.hierarquia, data).pipe(
      tap(() => this.domainCache.invalidateDominio('CARGO_REFERENCIA'))
    );
  }

  removerHierarquia(idTipoEntidade: number, idSubTipoEntidade?: number) {
    const queryParams = idSubTipoEntidade ? `?tipoSubentidadeId=${idSubTipoEntidade}` : '';
    return this.apiService.delete(`${route.removerHierarquia(idTipoEntidade)}${queryParams}`).pipe(
      tap(() => this.domainCache.invalidateDominio('CARGO_REFERENCIA'))
    );
  }

reativarItem(
  idTipoEntidade: number,
  idSubTipoEntidade?: number,
  idEspecificacaoSubTipoEntidade?: number
): Observable<void> {
  const queryParams: any = {};

  if (idSubTipoEntidade) {
    queryParams.idSubTipoEntidade = idSubTipoEntidade;
  }

  if (idEspecificacaoSubTipoEntidade) {
    queryParams.idEspecificacaoSubTipoEntidade = idEspecificacaoSubTipoEntidade;
  }

  const params = this.apiService.convertObjectToParams(queryParams);

  return this.apiService.get<void>(
    route.reativarHierarquia(idTipoEntidade),
    params
  ).pipe(
    tap(() => this.domainCache.invalidateDominio('CARGO_REFERENCIA'))
  );
}

  reativarItemNovo(id: number): Observable<any> {
    return this.apiService.get(route.reativarItem(id)).pipe(
      tap(() => this.domainCache.invalidateDominio('CARGO_REFERENCIA'))
    );
  }

  inativarItemNovo(id: number): Observable<any> {
    return this.apiService.get(route.inativarItem(id)).pipe(
      tap(() => this.domainCache.invalidateDominio('CARGO_REFERENCIA'))
    );
  }

  inativarItemNovoCargos(entidadeId: number, cargoId: number): Observable<any> {
    return this.apiService.delete(route.inativarItemNovoCargos(entidadeId, cargoId)).pipe(
      tap(() => this.domainCache.invalidateDominio('CARGO_REFERENCIA'))
    );
  }

  telasPermitidas() {
    return of([
      {
        codigo: 1,
        nome: 'Entidade'
      },
      {
        codigo: 2,
        nome: 'Pessoa'
      },
      {
        codigo: 3,
        nome: 'Vinculo'
      }
    ])
  }
}
